import { Box, For, IconButton, Link, Show } from '@chakra-ui/react';
import { useState } from 'react';
import { FaChevronDown, FaChevronRight, FaTrash } from 'react-icons/fa';
import FaceliftBadge from '@/components/Atoms/FaceliftBadge';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftText from '@/components/Atoms/FaceliftText';
import { useInvoiceCart } from '@/hooks/useInvoiceCart';
import type { InvoiceCheckoutData } from '@/types/invoice';

export interface SubInvoice {
  InvoiceID: string;
  Product: string;
  ProductID: string;
}

export interface CheckoutInvoiceItemProps {
  invoice: InvoiceCheckoutData;
  handleViewInvoice: (invoiceNumber: string, clinicQBOId: string) => void;
}

const CheckoutInvoiceItem: React.FC<CheckoutInvoiceItemProps> = ({
  invoice,
  handleViewInvoice,
}) => {
  const { removeInvoiceFromCart } = useInvoiceCart();
  const {
    InvoiceID,
    Invoice,
    Product,
    ProductID,
    updatedInvoiceAmount,
    subInvoices,
    pendingPayment,
    InvoiceStatus,
  } = invoice;
  const getBadgeColor = (status: string, pendingPayment?: boolean) => {
    if (pendingPayment || status === 'paid') return 'green.400';
    if (status === 'open') return 'blue.400';
    if (status === 'pending') return 'orange.400';
    return 'red.400';
  };

  const [expanded, setExpanded] = useState(false);
  const hasMultiple = subInvoices && subInvoices.length > 1;

  return (
    <Box
      mb={4}
      p={4}
      borderRadius="md"
      border="1px solid"
      borderColor={{ base: 'gray.200', _dark: 'gray.700' }}
      bg={{ base: 'gray.50', _dark: 'gray.800' }}
    >
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box display="flex" alignItems="flex-start" gap={2} flex={1}>
          <Show when={hasMultiple}>
            <IconButton
              aria-label={expanded ? 'Collapse' : 'Expand'}
              size="sm"
              variant="ghost"
              onClick={() => setExpanded((v) => !v)}
              mt={1}
            >
              {expanded ? <FaChevronDown /> : <FaChevronRight />}
            </IconButton>
          </Show>
          <Box flex={1}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <FaceliftText fontWeight="bold" fontSize="2xl">
                <Link color="{colors.brand.solidSecondary}" onClick={() => handleViewInvoice(InvoiceID, invoice.clinicQBOId)}
                  style={{ cursor: 'pointer' }}>
                  {Invoice}
                </Link>
              </FaceliftText>
              <FaceliftBadge
                status={InvoiceStatus}
                badgeColor={getBadgeColor(InvoiceStatus, pendingPayment)}
              />
              {invoice.State && (
                <FaceliftText fontSize="sm" fontWeight="medium">
                  {invoice.State.toUpperCase()}
                </FaceliftText>
              )}
            </Box>
            <Box>
              <Show when={!hasMultiple}>
                <FaceliftText fontWeight="bold" fontSize="md">
                  {Product} - {ProductID}
                </FaceliftText>
              </Show>
              <Show when={hasMultiple}>
                <FaceliftText fontWeight="bold" fontSize="md">
                  Multiple Products
                </FaceliftText>
              </Show>
            </Box>
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-end"
          gap={2}
          minW="150px"
        >
          <FaceliftText fontSize="2xl" fontWeight="bold">
            {updatedInvoiceAmount >= 0
              ? `$${updatedInvoiceAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : `-$${Math.abs(updatedInvoiceAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </FaceliftText>
          <FaceliftIconButton
            aria-label="Delete Invoice"
            color="red.400"
            variant="ghost"
            bg="transparent"
            border={'1px solid'}
            size="sm"
            onClick={() => removeInvoiceFromCart(InvoiceID)}
          >
            <FaTrash />
          </FaceliftIconButton>
        </Box>
      </Box>

      <Show when={hasMultiple}>
        <Box
          maxHeight={expanded ? '500px' : '0'}
          overflow="hidden"
          transition="max-height 0.3s ease-in-out, opacity 0.3s ease-in-out"
          opacity={expanded ? 1 : 0}
        >
          <Box
            mt={3}
            pt={3}
            borderTop="1px solid"
            borderColor={{ base: 'gray.200', _dark: 'gray.600' }}
            ml={hasMultiple ? 10 : 0}
          >
            <For each={subInvoices}>
              {(subItem) => (
                <Box
                  key={subItem.InvoiceID}
                  mb={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <FaceliftText fontWeight="medium" fontSize="sm">
                      {subItem.Product} - {subItem.ProductID}
                    </FaceliftText>
                  </Box>
                </Box>
              )}
            </For>
          </Box>
        </Box>
      </Show>
    </Box>
  );
};

export default CheckoutInvoiceItem;
